// A stored race, packed small enough to keep and to send.
//
// WHY THIS EXISTS, in numbers. A race written in the `MatchLine` shape every
// other sport is stored in costs about 2.3 KB: nine price rows of nine fields,
// a runners map, and every identity field spelled out. One year of AU/NZ
// greyhounds is 21,695 races and 50 MB of NDJSON — and 2025 is one year of one
// code. That is 50 MB read into a 512 MiB Cloud Run service to merge a month
// onto it, 50 MB (4.5 MB gzipped) if anybody ever asks `/api/history` for it,
// and ~130 MB of objects once parsed. A Premier League season is 300 KB.
//
// So a race is stored PACKED, and the packed form is what travels:
//
//   { k, t, v, n, r: [[cloth, bsp | null], …], w?: [cloth, …],
//     g?: [[cloth, ratedPrice, earlySpeed | null], …] }
//
//   k  matchKey — `racing_horse:2026-08-01:belmont:R1`, which already carries
//      the sport, the Sydney meeting date (so the season) and the venue slug,
//      so none of those are stored twice.
//   t  kickoff, ISO.
//   v  the venue as it is spelled on screen ("Sandown Hillside"); the slug is
//      in `k`. This is the one field the coordinator's sketch had as the slug —
//      it is the same bytes and strictly more information.
//   n  race number.
//   r  one pair per runner that took part: saddlecloth, and the Betfair
//      starting price or null where there was no offer. A runner with no price
//      stays in the field — it is a runner nobody priced, not a runner that did
//      not run.
//   w  the winners' saddlecloths. More than one IS the dead heat. Absent on a
//      race nobody has settled, which is what lets a later run complete it.
//   g  the Betfair Hub's own ratings for the race, where they have been
//      ingested (`--ratings`): the rated price per saddlecloth and its
//      early-speed figure. Absent on every race nobody has rated, which is
//      every race until that flag is run, and absent for harness always — the
//      Hub rates the gallops and the dogs and nothing else. A SECOND OPINION,
//      never a result: it is what `ratingDisagreement` is measured from and it
//      changes no price.
//
// That is ~210 bytes a race, an eleventh of the full shape.
//
// WHAT IS DROPPED, and why it is safe: the runners' NAMES (a backtest settles
// on `matchKey` and saddlecloth and never prints one — a leg in the ledger is
// `{ matchKey, side, price, won }`), the BSP volume, the race name and the
// distance. All four are one command from the free source if they are ever
// wanted (`scripts/ingest-racing-history.mjs --month`). An unpacked runner is
// named for its saddlecloth — "No. 4" — so nothing downstream can print the
// word "undefined" at a reader.
//
// The engine reads packed lines through `unpackRace`, one DAY at a time
// (`backtest.js`), so a season is never expanded whole.

import { RACING_OVERROUND_BAND } from '../racing/board.js';

/** The one book this history is, and the only one it will ever be. */
export const BSP_BOOK = 'betfair_ex_au';

/** What `/api/history` calls this shape, so a reader of the raw JSON can ask. */
export const PACKED_RACE_FORMAT = 'packed-race-v1';

/**
 * Is this stored line a packed race?
 *
 * Shape, not a version flag: `r` is an array of pairs and `k` is a key, and no
 * `MatchLine` has ever had either. A format that changes gets a new name in
 * `PACKED_RACE_FORMAT` and a new field to detect.
 */
export const isPackedRace = (line) => Array.isArray(line?.r) && typeof line?.k === 'string';

/** The kickoff of a stored line, packed or not. */
export const kickoffOf = (line) => (isPackedRace(line) ? line.t : line?.kickoff);

/** The matchKey of a stored line, packed or not. */
export const keyOf = (line) => (isPackedRace(line) ? line.k : line?.matchKey);

/** `racing_horse:2026-08-01:belmont:R1` → its four parts. */
export function partsOfRaceKey(key) {
  const [sport, date, venueId, race] = String(key ?? '').split(':');
  return { sport, date, venueId, raceNumber: Number(String(race ?? '').replace(/^R/, '')) };
}

const runnerId = (cloth) => `r${cloth}`;

/** A full `MatchLine` race → the packed form. */
export function packRace(line) {
  const priceOf = (selection) => {
    const row = (line.prices ?? []).find((p) => p.selection === selection);
    return row ? row.price : null;
  };
  const clothOf = (selection) => {
    const runner = line.runners?.[selection];
    return Number.isFinite(runner?.number) ? runner.number : Number(String(selection).slice(1));
  };

  const packed = {
    k: line.matchKey,
    t: line.kickoff,
    v: line.venue ?? null,
    n: line.raceNumber ?? partsOfRaceKey(line.matchKey).raceNumber,
    r: (line.selections ?? []).map((selection) => [clothOf(selection), priceOf(selection)]),
  };

  // The ratings ride along where a line carries them, in the same saddlecloth
  // order the prices are in.
  const rated = (line.selections ?? [])
    .map((selection) => {
      const rating = line.runners?.[selection]?.rating;
      return Number.isFinite(rating?.ratedPrice)
        ? [clothOf(selection), rating.ratedPrice, Number.isFinite(rating.earlySpeed) ? rating.earlySpeed : null]
        : null;
    })
    .filter(Boolean);
  if (rated.length) packed.g = rated;

  const result = line.result;
  if (result?.winner) {
    const winners = result.deadHeat?.length ? result.deadHeat : [result.winner];
    packed.w = winners.map((selection) => clothOf(selection));
  }
  return packed;
}

/** The packed form → the `MatchLine` the engine reads. */
export function unpackRace(packed) {
  const { sport, date, venueId } = partsOfRaceKey(packed.k);
  const venue = packed.v ?? venueId;
  const selections = [];
  const runners = {};
  const prices = [];

  // The Hub's rated price per saddlecloth, where the race has been rated.
  const rated = new Map((packed.g ?? []).map(([cloth, ratedPrice, earlySpeed]) =>
    [cloth, { ratedPrice, earlySpeed: earlySpeed ?? null }]));

  for (const [cloth, price] of packed.r ?? []) {
    const id = runnerId(cloth);
    selections.push(id);
    // Named for the saddlecloth, because the name is not kept. Honest, and it
    // can never print as "undefined" or "null" beside a price.
    runners[id] = { name: `No. ${cloth}`, number: cloth, scratched: false };
    // A SECOND OPINION ON THE RUNNER, and only where somebody published one.
    // `runners` is in `RACE_FIELDS`, so this rides into the match the engine
    // builds and `ratingDisagreement` can be measured off it.
    if (rated.has(cloth)) runners[id].rating = rated.get(cloth);
    if (price === null || price === undefined) continue;
    prices.push({
      bookmaker: BSP_BOOK,
      market: 'win',
      selection: id,
      price,
      lay: null,
      liquidity: null,
      kind: 'closing',
      capturedAt: packed.t,
      source: 'betfair-racing-csv',
    });
  }

  const line = {
    matchKey: packed.k,
    sport,
    competition: `${venue} ${date}`,
    season: String(date ?? '').slice(0, 4),
    round: null,
    kickoff: packed.t,
    venue,
    home: null,
    away: null,
    selections,
    runners,
    overroundBand: [...RACING_OVERROUND_BAND],
    raceLabel: `${venue} R${packed.n}`,
    venueId,
    raceNumber: packed.n,
    prices,
    sources: ['betfair-racing-csv'],
  };

  if (packed.w?.length) {
    line.result = {
      homeScore: null,
      awayScore: null,
      winner: runnerId(packed.w[0]),
      deadHeat: packed.w.length > 1 ? packed.w.map(runnerId) : null,
      status: 'final',
    };
  }
  return line;
}

/** A stored line as the engine wants it: unpacked if it was packed. */
export const asMatchLine = (line) => (isPackedRace(line) ? unpackRace(line) : line);

/**
 * Two packed races with one key, folded.
 *
 * A race is one atomic record from one source, so there is no price union to do
 * the way `mergeMatch` does for a fixture: the existing record stands, and the
 * incoming one only fills in a RESULT it does not have yet (an interim file
 * re-read once the placings are in). Re-ingesting a month is therefore a
 * byte-identical rewrite, which is the whole point.
 */
export function mergePackedRace(existing, incoming) {
  let out = existing;
  if (!existing.w?.length && incoming?.w?.length) out = { ...out, w: [...incoming.w] };
  // The ratings arrive in their own pass, months or years after the prices, and
  // they only ever fill a gap: a race that already carries them keeps what it
  // has, so re-running `--ratings` is a byte-identical rewrite.
  if (!existing.g?.length && incoming?.g?.length) out = { ...out, g: incoming.g.map((r) => [...r]) };
  return out;
}
